// REACT
import { useEffect, useState } from 'react';

// YARN
import { toast } from 'react-toastify';
import AddIcon from '@mui/icons-material/Add';
import { Button } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';

// SERVICES
import CultureGroupCard from './CultureGroupCard';
import { getCultureGroupList, getCultures } from 'services/API/Cooperative';
import AddEditCultureGroupModal from './AddEditCultureGroup';
import useCultureGroupAction from 'utils/hooks/useCultureGroupAction';
import './cultureGroup.scss';
import useAutoComplete from 'utils/hooks/useAutoComplete';

// INTERFACES
import { ICulture } from 'components/generics/Interface/ICulture';
import { IErrorResponse } from 'components/generics/Interface/Commons/IErrorResponse';

// THEMES
import { customButtonTheme } from 'assets/styles/themes/generic_button_mui';
import { ICultureGroup } from 'components/generics/Interface/ICultureGroup';

type CultureGroupParams = {
  ownerId: number;
  cultures: ICulture[];
  setCooperativeCultures: Function;
};

type editDataType = {
  id?: number | null;
  name: string;
  cultures: number[];
};

const CultureGroup = ({ ownerId, cultures, setCooperativeCultures }: CultureGroupParams) => {
  const [cultureGroup, setCultureGroup] = useState<ICultureGroup[]>([]);
  const [cultureGroupFiltered, setCultureGroupFiltered] = useState<ICultureGroup[]>(cultureGroup);
  const [modal, setModal] = useState<boolean>(false);
  const [editData, setEditData] = useState<boolean | undefined | null | any>(null);

  const formSuccess = async () => {
    setModal(false);
    setEditData(null);
    loadData();
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const results = await Promise.all([
        getCultureGroupList(ownerId),
        getCultures(ownerId, false),
      ]);
      setCultureGroup(results[0].data.data);
      setCooperativeCultures(results[1].data?.data);
    } catch (err) {
      const apiError = err as IErrorResponse;
      toast.error(apiError.message);
    }
  };

  const { renderDeleteDialog, handleDeleteDialogOpen, handleEditDialogOpen } =
    useCultureGroupAction(ownerId, cultureGroup, loadData, setModal, setEditData);

  const resetCultureGroupValue = () => {
    setInputValue('');
    setOptions(filterData('', cultureGroup));
  };

  const { renderAutoComplete, inputValue, setInputValue, filterData, setOptions } =
    useAutoComplete<ICultureGroup>({
      data: cultureGroup,
      getOptionLabel: (option) => option.name,
      resetValue: resetCultureGroupValue,
      isMetaculture: false,
    });

  useEffect(() => {
    if (inputValue) {
      const filteredGroups = cultureGroup?.filter((group) =>
        group.name.toLowerCase().includes(inputValue.toLowerCase())
      );
      setCultureGroupFiltered(filteredGroups);
    } else {
      setCultureGroupFiltered(cultureGroup);
    }
  }, [inputValue, cultureGroup]);

  const filterCulturesExistingInGroups = (
    cultures: ICulture[],
    cultureGroups: ICultureGroup[],
    editData: editDataType
  ): ICulture[] => {
    const cultureIdsInGroups = cultureGroups
      ?.filter((group) => !editData || group.id !== editData.id)
      ?.flatMap((group) => group?.cultures?.map((culture) => culture.id));

    return cultures?.filter((culture) => !cultureIdsInGroups?.includes(culture.id));
  };

  const filteredCultures = filterCulturesExistingInGroups(cultures, cultureGroup, editData);

  return (
    <div className="section">
      <div>
        <div className="override">
          <h1 className="title_section title_section_culture">Groupe de cultures</h1>
          <div className="contain_header_icon search-actions-wrapper">
            {renderAutoComplete()}
            <ThemeProvider theme={customButtonTheme}>
              <Button
                onClick={() => {
                  setModal(!modal), setEditData(null);
                }}
              >
                Ajouter <AddIcon />
              </Button>
            </ThemeProvider>
          </div>
        </div>
        <div className="card-container-cultureGroup">
          <CultureGroupCard
            cultureGroup={cultureGroupFiltered}
            onDeleteClick={handleDeleteDialogOpen}
            onEditClick={handleEditDialogOpen}
          />
        </div>
      </div>
      {modal && (
        <AddEditCultureGroupModal
          modal={modal}
          setModal={setModal}
          onSubmitSuccess={formSuccess}
          edit={editData}
          cooperativeId={ownerId}
          cultures={filteredCultures}
          cultureGroup={cultureGroup}
        />
      )}
      {renderDeleteDialog()}
    </div>
  );
};

export default CultureGroup;
